import { DEFAULT_TEMPLATE_ID, FullCVWithTemplate, TemplateId } from '@cibli/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { fetchCV } from '@/services/interview';

import { getTemplate } from '../templates/registry';
import TemplateHost from '../templates/TemplateHost';

interface Props {
  id?: string;
}

export function CVPreviewPage({ id }: Props) {
  const routeParams = useParams();
  const effectiveId = id ?? routeParams.id;
  const { cvTemplateId, cvTemplateParams } = useCVTemplateContext();
  const [isLoading, setIsLoading] = useState(true);
  const [cv, setCv] = useState<FullCVWithTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templateId = useMemo(
    () => (cv?.template || cvTemplateId || DEFAULT_TEMPLATE_ID) as TemplateId,
    [cvTemplateId, cv?.template],
  );

  const initialParams = useMemo(() => {
    return cv?.templateParams || cvTemplateParams;
  }, [cvTemplateParams, cv?.templateParams]);

  // Resolve template and keep params state BEFORE any early return to satisfy Rules of Hooks
  const template = useMemo(() => getTemplate(templateId), [templateId]);
  const params = useMemo(() => {
    const initialParsed = template.schema.safeParse(initialParams ?? template.defaults);
    return initialParsed.success ? initialParsed.data : template.defaults;
  }, [initialParams, template]);

  useEffect(() => {
    const getCVData = async () => {
      if (!effectiveId) return;
      try {
        setIsLoading(true);
        const data = await fetchCV(effectiveId);
        setCv(data);
      } catch (err) {
        console.error('Error fetching CV:', err);
        setError('Failed to load CV. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    getCVData();
  }, [effectiveId]);

  if (isLoading) {
    return <Loader2 className="size-10 animate-spin" />;
  }

  if (error || !cv) {
    return <div className="text-xl text-red-500">{error || 'CV introuvable'}</div>;
  }

  return (
    <div className="w-full flex justify-center bg-white print:block print:w-auto print:m-0 print:p-0 print:bg-white">
      <div className="w-[210mm] min-h-[297mm] print:w-[210mm] print:min-h-[297mm]">
        <TemplateHost cv={cv} mode="print" templateId={templateId} initialParams={params} />
      </div>
    </div>
  );
}
